// Guide — a task-oriented how-to that gets a new operator productive fast. It stays
// in the imperative voice (do this, read that) and hands off the "how far do I trust
// it" depth to the Methods & Limitations page via inline helpLink() deep-links and a
// closing pointer. Content-only: main.ts supplies the app header, so this renders
// just the <div class="doc"> body. Class-only styling (CSS is written separately),
// so it re-casts across the day / dusk / night bridge palettes.

import type { SiteNavOptions } from "./site.js";
import { helpLink } from "../help/popover.js";
import type { HelpKey } from "../help/types.js";
import { FIGURES } from "./manual-figures.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A paragraph that can weave inline help-links between runs of plain text. */
function para(...parts: (string | Node)[]): HTMLParagraphElement {
  const p = el("p");
  for (const part of parts) {
    p.append(typeof part === "string" ? document.createTextNode(part) : part);
  }
  return p;
}

/** An ordered list of numbered steps. */
function steps(items: (string | Node)[]): HTMLOListElement {
  const ol = el("ol");
  for (const item of items) {
    const li = el("li");
    li.append(typeof item === "string" ? document.createTextNode(item) : item);
    ol.appendChild(li);
  }
  return ol;
}

/** An unordered list (for "what the panels show"-style enumerations). */
function bullets(items: (string | Node)[]): HTMLUListElement {
  const ul = el("ul");
  for (const item of items) {
    const li = el("li");
    li.append(typeof item === "string" ? document.createTextNode(item) : item);
    ul.appendChild(li);
  }
  return ul;
}

/** One `<section class="doc-section">` opened by an `<h2>`. */
function section(title: string, ...body: Node[]): HTMLElement {
  const s = el("section", "doc-section");
  s.appendChild(el("h2", undefined, title));
  for (const node of body) s.appendChild(node);
  return s;
}

/**
 * A captured figure with its numbered callouts.
 *
 * Badges live in the PNG as bare digits; their explanations are real HTML here,
 * so the manual stays searchable, re-themes with the bridge palettes and reads
 * aloud — none of which text baked into an image can do. The `value` on each
 * <li> is the badge number rather than a positional count, so the list cannot
 * drift out of step with the picture.
 *
 * A missing figure renders as nothing at all. The prose around it always stands
 * on its own, and a broken-image icon in an operator's manual reads as neglect.
 */
function figure(id: string): HTMLElement | null {
  const f = FIGURES[id];
  if (f === undefined) return null;

  const fig = el("figure", "doc-fig");
  const img = el("img");
  img.src = `/${f.file}`;
  img.alt = f.alt;
  img.loading = "lazy";
  fig.appendChild(img);

  const cap = el("figcaption");
  cap.appendChild(el("p", "fig-cap", f.caption));
  if (f.callouts.length > 0) {
    const ol = el("ol", "fig-callouts");
    for (const c of f.callouts) {
      const li = el("li", undefined, c.note);
      li.value = c.n;
      ol.appendChild(li);
    }
    cap.appendChild(ol);
  }
  fig.appendChild(cap);
  return fig;
}

/** figure() as a spreadable arg list — drops cleanly when the shot is absent. */
function fig(id: string): Node[] {
  const f = figure(id);
  return f === null ? [] : [f];
}

/** A glossary row: term + a one-line gloss that deep-links to its Methods anchor. */
function glossRow(dl: HTMLDListElement, term: string, gloss: string, key: HelpKey): void {
  const dt = el("dt");
  dt.appendChild(helpLink(key, term));
  dl.appendChild(dt);
  dl.appendChild(el("dd", undefined, gloss));
}

export function mountGuide(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "Guide — SeaState";

  const doc = el("div", "doc");

  doc.appendChild(el("h1", undefined, "Operator's guide"));
  doc.appendChild(
    el(
      "div",
      "doc-lede",
      "How to get a workability read out of SeaState in a few clicks. This page is the " +
        "quick start; when you want to know how far to trust a number, follow the links " +
        "into Methods & Limitations.",
    ),
  );

  // 1 — Getting started ---------------------------------------------------------
  doc.appendChild(
    section(
      "Getting started",
      para(
        "SeaState is client-only — the console renders your fleet, so you sign in first. " +
          "There are two working surfaces:",
      ),
      bullets([
        "The console — the map plus the stacked forecast blocks. This is where you read a point and get a go / no-go call.",
        "The Vessels page — where you keep your fleet honest: dimensions, speeds, the operations each hull performs, and the condition limits the workability read is measured against.",
      ]),
      steps([
        "Open the console and sign in with your client login.",
        "Set your vessels and their limits once on the Vessels page (see below) — then live in the console.",
      ]),
      ...fig("console-overview"),
      para(
        "The strip down the left is the workspace rail. Each button swaps the panel " +
          "beside it — Site, SVP, Fleet, Observations, Tropical, Systems — and the number " +
          "keys 1–9 do the same thing faster. Clicking the workspace you are already in " +
          "closes the panel and gives the map the whole window back.",
      ),
    ),
  );

  // 1b — The map and its colour --------------------------------------------------
  doc.appendChild(
    section(
      "The map, and what its colour means",
      para(
        "The colour under the map is a real forecast field, not a decoration: a global " +
          "0.25° grid re-fetched every three hours, painted for whichever hour the timeline " +
          "playhead is sitting on. The arrows over it are that same field's direction.",
      ),
      para(
        "Two habits are worth forming. First, check the legend before reading the colour — " +
          "it names the layer, the model, and the run the field came from, so you always know " +
          "how old the picture is. Second, remember the field is 0.25°: about 15 nautical miles " +
          "a cell. It is the right tool for choosing between areas and the wrong one for " +
          "deciding where to put a mooring.",
      ),
      para(
        "Zooming out coarsens the grid by taking the WORST value in each block rather than " +
          "sampling one cell, so a storm peak can never vanish just because you zoomed out. " +
          "A coarse view can over-warn. It cannot under-warn.",
      ),
    ),
  );

  // 1c — The timeline ------------------------------------------------------------
  doc.appendChild(
    section(
      "The timeline and the playhead",
      para(
        "Everything on screen — the map colour, the workability ribbon, every chart — is " +
          "tied to one shared timeline running along the top of the chart stack. Moving the " +
          "playhead moves all of them together, so the map and the charts can never be showing " +
          "you two different moments.",
      ),
      steps([
        "Click anywhere on the date strip or the confidence band below it to jump the playhead to that hour.",
        "Press and drag along either one to scrub — the map, the charts and the workability ribbon all follow together.",
        "Use ‹ and › to step an hour at a time, or ▶ to run the forecast forward as an animation — the quickest way to see a system arrive.",
        "Press now to snap back to the current hour, or clear to drop the held time and return to live.",
      ]),
      para(
        "Moving your pointer across a chart does not move the playhead. Hovering reads " +
          "that chart — the hour under your pointer, its numbers, and the \"why\" behind any " +
          "flag — and leaves everything else where you put it. That separation is deliberate: " +
          "it means you can park the playhead on the hour you care about and then point at " +
          "things without it running away from you.",
      ),
      para(
        "Under the date row is the confidence band, and it is the most important thing on " +
          "the timeline. It divides the week into past, 0–48 h, 48–120 h and 120 h+, because " +
          "a forecast four hours out and a forecast six days out are not the same kind of " +
          "statement. Read a green hour at 120 h+ as \"nothing is currently expected to stop " +
          "us\", never as a plan.",
      ),
    ),
  );

  // 1d — The work window ---------------------------------------------------------
  doc.appendChild(
    section(
      "The work window",
      para(
        "A job is not an instant, it is a stretch of hours. The Window briefing panel " +
          "asks the console for one: it starts wherever the playhead is, runs for the length " +
          "you set, and answers in plain language for that span.",
      ),
      steps([
        "Put the playhead on the hour you would start.",
        "Set Length to how long the job needs — the panel reads out the start hour so you can see what you are asking about.",
        "Read the briefing: wind and sea through the window, how much the models disagree, and whether the daylight holds.",
      ]),
      para(
        "The window is drawn on the timeline and bookended on every chart, so you can see " +
          "the span you are asking about against the wind, the waves and the daylight at once. " +
          "Slide the playhead forward a few hours and the whole answer moves with it, which is " +
          "the fastest way to find the window that works rather than checking one you guessed.",
      ),
    ),
  );

  // 2 — Reading a point ---------------------------------------------------------
  doc.appendChild(
    section(
      "Reading a point",
      para("Click anywhere at sea and the console loads a 7-day, hourly, multi-model forecast for that exact spot."),
      steps([
        "Click an ocean point on the map — or click an NDBC buoy to also pull that station's live observations for comparison.",
        "Read the location primer: where you are (seabed feature, water body, jurisdiction), nearby infrastructure, nearest port, and a month climatology of typical winds and seas.",
        "Pick the vessel and operation you're planning from the selectors. Everything downstream re-scopes to that choice.",
      ]),
      para("The stacked meteogram blocks each read one layer of the forecast:"),
      bullets([
        "Wind — each model as its own line, with the GEFS ensemble band showing how much the models agree.",
        "Wave — significant height per model plus the shaded model min–max envelope.",
        "Period — wave period in seconds, each line labelled with the quantity that model publishes: GFS-Waves reports peak period, the other three energy period. They are not converted to a common one, so read the label before comparing them.",
        "Current — surface current speed with set-direction arrows.",
        "Daylight — sun elevation, sunrise/sunset, darkness bands, and visibility.",
        "Sea character — swell versus wind-sea, flagging crossed-sea hours.",
      ]),
      para(
        "Hover any hour on a chart to read that hour's \"why\" — the per-hour numbers behind a flag, " +
          "for example a squall hour spelled out as its convective precip and gust-over-mean spread.",
      ),
      ...fig("reading-a-point"),
      para(
        "Each chart names its own sources underneath. Where several models are drawn, they " +
          "are drawn SEPARATELY and never averaged into a single line: the width between " +
          "them is information. Two models agreeing at 30 knots is a very different fact " +
          "from two models straddling 30, and a blended line would hide exactly that.",
      ),
    ),
  );

  // 3 — The workability read ----------------------------------------------------
  doc.appendChild(
    section(
      "The workability read",
      para(
        "With a vessel and operation selected, the Workability track at the head of the chart stack " +
          "paints an hourly go / no-go ribbon on the shared timeline — directly above the wind and " +
          "wave charts it is computed from — and the Site panel lists the next long-enough weather " +
          "windows. Read the bands like this:",
      ),
      bullets([
        "Go — conditions sit inside your limits with room to spare.",
        "Marginal — workable but tightening; watch the trend and the ensemble spread.",
        "Poor / No-go — outside your limits, or too little model agreement to commit.",
      ]),
      para(
        "The ribbon is coloured hour by hour, but the figure in the track header answers for the " +
          "whole work window: it grades the worst hour between the playhead and the end of the " +
          "window, and names the hour that worst falls on — \"62% · worst Thu 04:00 · Marginal\". " +
          "A job is stopped by its worst moment rather than its first, so the header will not say Go " +
          "while there is red inside the bookends. Move the playhead until that worst hour improves " +
          "and you have found a window that works.",
      ),
      para(
        "This read is scoped: it is measured against the selected vessel and operation and your own " +
          "client limits, and squall hours are capped harder for crane and personnel work than for transit. ",
        helpLink("wk.verdict", "How it's computed, and why it's advisory"),
        ", not a clearance to sail.",
      ),
      ...fig("workability"),
      para(
        "The percentages in the legend are ensemble agreement, not a chance of good weather: " +
          "Go ≥ 90% means at least nine in ten forecast members keep the hour inside your " +
          "limits. Marginal is the band to slow down on — it usually means the members " +
          "disagree rather than that conditions are borderline, and the fix for disagreement " +
          "is another model run, not a decision.",
      ),
    ),
  );

  // 4 — Setting your limits -----------------------------------------------------
  doc.appendChild(
    section(
      "Setting your limits",
      para(
        "The workability read is only as good as the numbers it's measured against, so set them on " +
          "the Vessels page.",
      ),
      steps([
        "Enter each vessel's dimensions and speeds, and flag DP, day-only, and nearshore where they apply.",
        "Give each vessel the operations it actually performs — coring, CPT, survey, transfer.",
        "Set the wind, wave, and duration limits per vessel and per operation.",
      ]),
      ...fig("setting-limits"),
      para(
        "Until you set your own numbers, the console runs a seeded default and says so with a ",
        el("strong", undefined, "⚠ using the seeded default limit"),
        " warning on the workability panel. Treat that as provisional — the default is a size/DP heuristic, not your operations team's judgment. ",
        helpLink("wk.default", "Why the default warning matters"),
        ".",
      ),
    ),
  );

  // 5 — Range rings -------------------------------------------------------------
  doc.appendChild(
    section(
      "Range rings",
      para(
        "To see how far the selected vessel can steam from the clicked point, turn on the range rings " +
          "in the Overlays panel. They draw concentric 1 / 2 / 3-day steaming reaches at the vessel's " +
          "cruise speed (24 h/day), each labelled days · nm. It is straight-line reach — no routing and " +
          "no weather — so read it as an outer bound, not a passage plan.",
      ),
    ),
  );

  // 6 — Sound-velocity profiles -------------------------------------------------
  doc.appendChild(
    section(
      "Sound-velocity profiles (SVP)",
      para(
        "The primer's sound-speed read and depth chart are for SV-cast planning — a planning-grade look " +
          "at the thermocline and mixed layer before you commit an acoustic survey. ",
        helpLink("svp.overview", "More on sound-velocity profiles"),
        ".",
      ),
      ...fig("svp"),
      para(
        "When a recent Argo float has surfaced near your point, its real cast is drawn " +
          "beside the modelled curve — a separate line, labelled with the float, its age and " +
          "its distance. It is never averaged into the model. An observation and a model are " +
          "different kinds of claim, and blending them would throw away the one thing that " +
          "makes the observation worth having.",
      ),
    ),
  );

  // 7 — Bridge display ----------------------------------------------------------
  doc.appendChild(
    section(
      "Bridge display",
      para(
        "For operating at night, use the Day / Dusk / Night / Auto toggle in the header. It re-casts the " +
          "whole console — charts and basemap included — so it won't wreck night vision on the bridge. " +
          "Auto follows the sun at your point and re-checks itself, and your choice is remembered between " +
          "sessions.",
      ),
      ...fig("bridge-display"),
      para(
        "One thing deliberately does not re-theme: the go / marginal / no-go colours. " +
          "Everything else shifts for night vision, but a colour that means \"stop\" has to " +
          "mean it identically on a dark bridge at 0300 and at a desk at midday.",
      ),
    ),
  );

  // 7b — Comparing areas ---------------------------------------------------------
  doc.appendChild(
    section(
      "Comparing areas",
      para(
        "Reading a point answers \"can we work here\". The Waypoints workspace answers the " +
          "earlier question — \"which of these should we be at\" — by applying the same " +
          "vessel-and-operation judgement across candidate areas instead of one position, " +
          "so the comparison is like for like rather than eyeballed off the map colour.",
      ),
      ...fig("where-to-work"),
    ),
  );

  // 7c — Buoys and observations ---------------------------------------------------
  doc.appendChild(
    section(
      "Checking the forecast against a buoy",
      para(
        "A forecast that has never been checked against an instrument is an opinion. Turn " +
          "on Stations in the Observations workspace and the map shows live buoys — NDBC in " +
          "US waters, AODN around Australia. Click one instead of open water and the console " +
          "pulls that station's real observations alongside the models.",
      ),
      para(
        "The Forecast vs buoy panel then stacks the two: what the models said, and what the " +
          "sea actually did. Observed values are drawn as points and never merged into a " +
          "model line. If the models have been running a half-metre light at that buoy all " +
          "week, you want to see that before you plan against them, not after.",
      ),
    ),
  );

  // 7d — When something looks wrong -----------------------------------------------
  doc.appendChild(
    section(
      "When something looks wrong",
      para(
        "SeaState draws from a lot of independent upstreams, and they do not all fail at " +
          "once — or loudly. The Systems workspace lists every one of them with the age of " +
          "what it last said, so an empty panel always has an explanation.",
      ),
      ...fig("systems-health"),
      para(
        "The distinction that matters: a panel can be empty because a source is down, or " +
          "because the honest answer is nothing — no tropical systems, no Argo float within " +
          "range, no buoy nearby. Systems tells you which, so you never read an outage as " +
          "good news. Nothing on that panel polls anything; it records the fetches the " +
          "console was already making, so a struggling link is never made worse by being " +
          "watched.",
      ),
    ),
  );

  // 8 — Glossary ----------------------------------------------------------------
  const dl = el("dl");
  glossRow(
    dl,
    "Workability",
    "The go / marginal / no-go read for a chosen vessel and operation against your limits.",
    "wk.verdict",
  );
  glossRow(
    dl,
    "GEFS spread",
    "The width of the GEFS ensemble band — how much the forecast members disagree, i.e. how settled the atmosphere is.",
    "fc.gefs",
  );
  glossRow(
    dl,
    "Sea character",
    "The split between long-period swell and locally-driven wind-sea, and the crossed-sea hazard when they run apart.",
    "fc.seaChar",
  );
  glossRow(
    dl,
    "Squall risk",
    "A per-hour 0–3 score for convective gusts, from GFS convective fields against the WMO squall criterion.",
    "fc.squall",
  );
  glossRow(
    dl,
    "Thermocline",
    "The depth band where temperature — and so sound speed — changes fastest; it steers your SV-cast planning.",
    "svp.thermocline",
  );
  glossRow(
    dl,
    "Mixed layer",
    "The near-surface layer of near-uniform sound speed above the thermocline.",
    "svp.mld",
  );
  glossRow(
    dl,
    "EEZ",
    "Exclusive Economic Zone — the maritime jurisdiction a point sits in, and whether it is contested or shared.",
    "map.eez",
  );
  glossRow(
    dl,
    "Range rings",
    "Concentric 1 / 2 / 3-day straight-line steaming reaches for the selected vessel.",
    "map.rings",
  );
  glossRow(
    dl,
    "Not for navigation",
    "SeaState is a planning aid, not a navigation or clearance system — the standing caveat on every read.",
    "fnd.notForNav",
  );
  doc.appendChild(section("Glossary", dl));

  // Closing pointer to Methods & Limitations ------------------------------------
  const methods = el("section", "doc-section");
  const line = el("p", "doc-lede");
  line.append(document.createTextNode("For data sources, model detail, and the limits of every read — "));
  const link = el("a", "doc-methods-link", "read Methods & Limitations →");
  link.href = "/methods";
  link.addEventListener("click", (e) => {
    e.preventDefault();
    opts.navigate("/methods");
  });
  line.appendChild(link);
  line.append(document.createTextNode("."));
  methods.appendChild(line);
  doc.appendChild(methods);

  container.appendChild(doc);
}
